import { Accordion, AccordionDetails, AccordionSummary, Grid, Radio, RadioGroup } from '@material-ui/core'
import { Formik, Form } from 'formik'
import React from 'react'
import styled, { css } from 'styled-components'
import { default as serviceLineMutation } from '../../../apolloClient/mutations/updatePrimaryServiceLine'
import { useAuth0 } from '../../../contexts/Auth0Context'
import { useOnboarding } from '../../../contexts/OnboardingContext'
import colors from '../../../shared/constants/colors'
import { SERVICE_LINES } from '../../../shared/constants/serviceLines'
import SERVICE_LINE_QUERY from '../../../apolloClient/queries/getServiceLine'
import FormActions from '../../../_global/FormActions'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

type ServiceLineProps = {
  initialValues?: any
}

const ServiceLineForm = (props: ServiceLineProps) => {
  const { logout } = useAuth0()
  const { step, back, next: nextStep, formRef, currentExpert } = useOnboarding()
  const [updateFreelancerServiceLine] = serviceLineMutation()

  const {
    initialValues: { serviceLine, legacyFreelancerId, isSaveAndExit, onboardingStep },
  } = props

  const handleSubmit = async (values) => {
    if (values.serviceLine) {
      await updateFreelancerServiceLine({
        variables: {
          freelancerId: legacyFreelancerId,
          serviceLine: values.serviceLine,
        },
        refetchQueries: [{ query: SERVICE_LINE_QUERY, variables: { id: currentExpert.id } }],
      }).then((result) => {
        if (values.isSaveAndExit) {
          logout()
        } else {
          if (step !== 7) {
            nextStep()
          }
        }
      })
    } else {
      if (values.isSaveAndExit) {
        logout()
      }
    }
  }

  return (
    <Formik
      validateOnMount={true}
      initialValues={{ serviceLine, isSaveAndExit }}
      onSubmit={handleSubmit}
      innerRef={formRef}
    >
      {(props) => {
        const { isSubmitting, isValid, values, handleChange, dirty } = props
        /*
                ServiceLine cannot be null in the backend, so the first time the user
                visits this page, we set the service line to '' on the front end. This way
                none of the radio buttons show up as selected, even though their is a default
                in the db. If the user doesn't choose a service line, and save and exits, when
                they return they will see the default backend service line radio button selected
                because this is not the first time they visited the page, but we cannot know if they
                clicked save and exit last time they visited.
                */
        values.serviceLine = onboardingStep < 2 && !dirty ? '' : values.serviceLine
        return (
          <Form autoComplete="off">
            <RadioGroup aria-label="serviceLine" name="serviceLine" value={values.serviceLine} onChange={handleChange}>
              {SERVICE_LINES.map((item) => {
                const selectedServiceLine = values.serviceLine === item.id
                return (
                  <StyledRadioButtonSelection
                    container
                    key={item.id}
                    alignItems="center"
                    data-selected={selectedServiceLine}
                  >
                    <ServiceLineItem label={item.label} text={item.text} id={item.id} />
                  </StyledRadioButtonSelection>
                )
              })}
            </RadioGroup>
            {step !== 7 && <FormActions step={step} back={back} isValid={isValid} isSubmitting={isSubmitting} />}
          </Form>
        )
      }}
    </Formik>
  )
}

type ServiceLineItemProps = {
  label: string
  text: string
  id: string
}

const ServiceLineItem = ({ label, text, id }: ServiceLineItemProps) => {
  const [expanded, setExpanded] = React.useState(false)
  return (
    <ServiceLineAccordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
        <Grid container alignItems="center">
          <Grid item xs={'auto'}>
            <Radio value={id} color="primary" size="small" onClick={(e) => e.stopPropagation()} />
          </Grid>
          <Grid item xs>
            {label}
          </Grid>
        </Grid>
      </AccordionSummary>
      <StyledInformationText>{text}</StyledInformationText>
    </ServiceLineAccordion>
  )
}

const StyledRadioButtonSelection = styled(Grid)`
  border: 1px solid ${colors.borderGray};
  border-radius: 3px;
  margin-bottom: 16px;
  padding: 8px;

  ${(props) =>
    props['data-selected'] &&
    css`
      border: 1px solid ${colors.paroBlack};
    `}
`

const StyledInformationText = styled(AccordionDetails)`
  font-size: 14px;
  padding: 0px 20px 20px 40px;
`

const ServiceLineAccordion = styled(Accordion)`
  background-color: transparent;
  box-shadow: none;
`

export default ServiceLineForm
